import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileCatExpanded(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
          : "bg-background-50/85 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <a
          href="/"
          className="flex items-center gap-3 group cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <i className="ri-stack-line text-background-50 text-base"></i>
          </div>
          <span className="font-label font-bold text-[15px] tracking-[0.15em] text-foreground-950 uppercase">
            Pandabuy<span className="text-primary-500">.fit</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="px-4 py-2 text-[13px] font-label font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            HOME
          </Link>

          {/* Categories Dropdown */}
          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="px-4 py-2 text-[13px] font-label font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap flex items-center gap-1 cursor-pointer"
            >
              CATEGORIES
              <i
                className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[720px] bg-background-50 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-background-200/60 p-6 z-50">
                <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeDropdown}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-900 hover:text-foreground-950 hover:bg-background-100 transition-all duration-200 group cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={cat.imageUrl}
                          alt={`${cat.name} Pandabuy Spreadsheet`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-[13px] font-label font-semibold whitespace-nowrap">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/pandabuy-spreadsheet-guide"
            className="px-4 py-2 text-[13px] font-label font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            SHOPPING GUIDE
          </Link>

          <a
            href="#articles"
            className="px-4 py-2 text-[13px] font-label font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            BLOG
          </a>
          <a
            href="#faq"
            className="px-4 py-2 text-[13px] font-label font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            SAFETY
          </a>
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-2.5">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-[13px] font-label font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Pandabuy Sheet
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          aria-label="Toggle menu"
        >
          <i
            className={`text-xl text-foreground-950 transition-all duration-300 ${
              mobileMenuOpen ? "ri-close-line" : "ri-menu-line"
            }`}
          ></i>
        </button>
      </div>

      {/* Mobile Menu - Enhanced */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 shadow-lg max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-0.5">
            <Link
              to="/"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3.5 text-[15px] font-label font-semibold text-foreground-950 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-home-4-line text-primary-600 text-sm"></i>
              </div>
              HOME
            </Link>

            <Link
              to="/pandabuy-spreadsheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3.5 text-[15px] font-label font-semibold text-foreground-950 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-book-open-line text-accent-600 text-sm"></i>
              </div>
              SHOPPING GUIDE
            </Link>

            {/* Mobile Categories - Expandable */}
            <div>
              <button
                onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
                className="flex items-center justify-between w-full px-4 py-3.5 text-[15px] font-label font-semibold text-foreground-950 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-apps-2-line text-secondary-600 text-sm"></i>
                  </div>
                  CATEGORIES
                </div>
                <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-300 ${mobileCatExpanded ? "rotate-180" : ""}`}></i>
              </button>

              {mobileCatExpanded && (
                <div className="mt-1 mb-1 mx-2 grid grid-cols-2 gap-1 bg-background-100 rounded-2xl p-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeDropdown}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-foreground-900 hover:text-foreground-950 hover:bg-background-50 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-[12px] font-label font-semibold leading-tight">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#articles"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3.5 text-[15px] font-label font-semibold text-foreground-950 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-article-line text-primary-600 text-sm"></i>
              </div>
              BLOG
            </a>

            <a
              href="#faq"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3.5 text-[15px] font-label font-semibold text-foreground-950 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-shield-check-line text-accent-600 text-sm"></i>
              </div>
              SAFETY
            </a>

            <div className="px-4 pt-4 pb-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 text-[14px] font-label font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Pandabuy Sheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}